import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';
import { 
  PlayIcon, 
  PauseIcon, 
  StopIcon,
  CogIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ClockIcon,
  UsersIcon,
  ScaleIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import MqttProfessional from './MqttProfessional';
import HardwareRegistry from './HardwareRegistry';
import HardwareSimulation from './HardwareSimulation';
import IODiagnostics from './IODiagnostics';
import HardwareLogging from './HardwareLogging';

interface SimulationConfig {
  id: string;
  lineId: string;
  lineName: string;
  stationNumber: number;
  enabled: boolean;
  infeedRate: number;
  infeedVariance: number;
  pocketRates: {
    pocket1: number;
    pocket2: number;
    pocket3: number;
    pocket4: number;
    waste: number;
  };
  pocketVariance: number;
  updateIntervalSeconds: number;
}

interface SimulationSession {
  id: string;
  name: string;
  status: 'stopped' | 'running' | 'paused';
  startedAt?: string;
  stoppedAt?: string;
}

interface HardwareServiceStatus {
  service: string;
  status: 'running' | 'stopped';
  activeSimulations: number;
  productionMode: string;
  currentShift: string;
  timestamp: string;
  error?: string;
}

interface Line {
  id: string;
  lineName: string;
  stationCount: number;
  hardwareId?: number;
}

interface ProductionDataSummary {
  production_date: string;
  shift: string;
  product_group_id: string;
  product_group_name: string;
  worker_count: number;
  total_seconds_worked: number;
  line_count: number;
  station_count: number;
  total_infeed_weight: number;
  total_outfeed_weight: number;
  data_points: number;
  production_time: number;
  stop_time: number;
  prepare_time: number;
  clean_time: number;
}

interface HardwareDataRecord {
  id: string;
  line_id: string;
  line_name?: string;
  hardware_id?: number;
  stationnumber: number;
  infeed_weight: number;
  total_outfeed_weight: number;
  shift: string;
  employee_id?: string;
  employee_name?: string;
  timestamp: string;
  created_at: string;
  pocket_number?: string;
  pocket_name?: string;
  pocket_product_id?: string;
  pocket_product_name?: string;
  pocket_display_name?: string;
  counts_toward_outfeed?: boolean;
}

type ActiveTab = 'hardware' | 'production-data' | 'io';
type HardwareSubTab = 'hardware-simulation' | 'mqtt-sparkplug' | 'hardware-data' | 'hardware-registry' | 'hardware-logging';
type HardwareDataSubTab = 'infeed' | 'outfeed';

const DiagnosticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('hardware');
  const [simulationConfigs, setSimulationConfigs] = useState<SimulationConfig[]>([]);
  const [currentSession, setCurrentSession] = useState<SimulationSession | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [activeLineTab, setActiveLineTab] = useState<string>(''); // Track active line tab
  const [isSimulationEnabled, setIsSimulationEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<SimulationConfig | null>(null);
  const [hardwareServiceStatus, setHardwareServiceStatus] = useState<HardwareServiceStatus | null>(null);
  const [productionData, setProductionData] = useState<ProductionDataSummary[]>([]);
  const [deletingItem, setDeletingItem] = useState<string | null>(null);
  const [lineModes, setLineModes] = useState<Record<string, string>>({});
  const [simulationStatus, setSimulationStatus] = useState<string>('Idle');
  const [setAllWorking, setSetAllWorking] = useState<boolean>(false);
  const [numberOfPockets, setNumberOfPockets] = useState<number>(3); // Default to 3 pockets
  const [defaultNumberOfStations, setDefaultNumberOfStations] = useState<number>(10); // Default to 10 stations
  
  // Hardware test state
  const [hardwareSubTab, setHardwareSubTab] = useState<HardwareSubTab>('hardware-simulation');
  const [hardwareDataSubTab, setHardwareDataSubTab] = useState<HardwareDataSubTab>('infeed');
  const [hardwareDataRecords, setHardwareDataRecords] = useState<HardwareDataRecord[]>([]);
  const [loadingHardwareData, setLoadingHardwareData] = useState(false);
  const [selectedHardwareLine, setSelectedHardwareLine] = useState<string>('');
  const [selectedHardwareDate, setSelectedHardwareDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const recordsPerPage = 50;
  // Removed unused Hardware Service Testing state variables
  
  // MQTT monitoring state
  const [mqttStatus, setMqttStatus] = useState<any>(null);
  const [mqttDevices, setMqttDevices] = useState<any[]>([]);
  const [selectedMqttLine, setSelectedMqttLine] = useState<string>('');

  // API URL construction
  const apiUrl = getApiUrl();

  // Fetch initial data
  useEffect(() => {
    // Removed simulation tab logic - now handled in hardware-simulation subtab
    if (false) {
      // These functions are now called from hardware-simulation component
      // Don't fetch line modes here - wait for lines to load
      
      // Poll status every 2 seconds
      const interval = setInterval(() => {
        fetchHardwareServiceStatus();
        fetchSimulationStatus(); // Also fetch the latest simulation session
        updateSimulationStatus();
        // Line modes will be fetched by the lines effect
      }, 2000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'production-data') {
      fetchProductionData();
    } else if (activeTab === 'hardware') {
      fetchLines();
      // Clear any previous test results
      
      // Fetch MQTT status if on MQTT subtab
      if (hardwareSubTab === 'mqtt-sparkplug') {
        fetchMqttStatus();
        fetchMqttDevices();
        
        // Poll MQTT status every 5 seconds
        const interval = setInterval(() => {
          fetchMqttStatus();
          fetchMqttDevices();
        }, 5000);
        
        return () => clearInterval(interval);
      } else if (hardwareSubTab === 'hardware-data') {
        // Don't auto-fetch - wait for user to click Load Data
        // No polling for hardware data logs - it's on-demand only
      }
    }
  }, [activeTab, hardwareSubTab, hardwareDataSubTab]);
  
  // Fetch line modes when lines are loaded
  useEffect(() => {
    if (lines.length > 0) {
      fetchLineModes();
      
      // Poll line modes every 2 seconds
      const interval = setInterval(() => {
        fetchLineModes();
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [lines]);

  // Update simulation status when mode or session changes
  useEffect(() => {
    updateSimulationStatus();
  }, [lineModes, currentSession]);

  const fetchLines = async () => {
    try {
      const response = await fetch(`${apiUrl}/lines`);
      const result = await response.json();
      if (result.data) {
        const lineData = result.data.map((line: any) => ({
          id: line.id,
          lineName: line.lineName || line.name,
          stationCount: line.numberOfStations || line.stationCount || 10,
          hardwareId: line.hardwareId
        }));
        setLines(lineData);
        // Set first line as active tab if not already set
        if (lineData.length > 0 && !activeLineTab) {
          setActiveLineTab(lineData[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
    }
  };

  const fetchSimulationConfigs = async () => {
    try {
      const response = await fetch(`${apiUrl}/simulation-configs`);
      const result = await response.json();
      if (result.data) {
        // Map the nested config structure to flat structure expected by DiagnosticsPage
        const mappedConfigs = result.data.map((item: any) => ({
          id: item.id,
          lineId: item.lineId,
          lineName: item.lineName,
          stationNumber: item.stationNumber,
          enabled: item.enabled,
          infeedRate: item.config?.infeedRate || 0,
          infeedVariance: item.config?.infeedVariance || 5,
          pocketRates: item.config?.pocketRates || {
            pocket1: 0,
            pocket2: 0,
            pocket3: 0,
            pocket4: 0,
            waste: 0
          },
          pocketVariance: item.config?.pocketVariance || 3,
          updateIntervalSeconds: item.config?.updateIntervalSeconds || 5
        }));
        setSimulationConfigs(mappedConfigs);
      }
    } catch (error) {
      console.error('Error fetching simulation configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSimulationStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/simulation-status`);
      const result = await response.json();
      if (result.data) {
        setCurrentSession(result.data.session);
        setIsSimulationEnabled(result.data.enabled);
      }
    } catch (error) {
      console.error('Error fetching simulation status:', error);
    }
  };

  const fetchHardwareServiceStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/hardware-service/health`);
      const result = await response.json();
      if (result.data) {
        setHardwareServiceStatus(result.data);
      }
    } catch (error) {
      console.error('Error fetching hardware service status:', error);
    }
  };

  const fetchMqttStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/mqtt/status`);
      const result = await response.json();
      if (result.data) {
        // Map status to connected boolean for UI
        const mqttData = {
          ...result.data,
          connected: result.data.status === 'connected'
        };
        setMqttStatus(mqttData);
      }
    } catch (error) {
      console.error('Error fetching MQTT status:', error);
      setMqttStatus({ connected: false, status: 'error', error: 'Failed to fetch MQTT status' });
    }
  };

  const fetchMqttDevices = async () => {
    try {
      const response = await fetch(`${apiUrl}/mqtt/devices`);
      const result = await response.json();
      if (result.data) {
        setMqttDevices(result.data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching MQTT devices:', error);
      setMqttDevices([]);
    }
  };


  const fetchLineModes = async () => {
    try {
      // Don't try to fetch if no lines loaded yet
      if (lines.length === 0) {
        console.log('No lines loaded yet, skipping mode fetch');
        return;
      }
      
      // Fetch mode for each line
      const modes: Record<string, string> = {};
      console.log(`Fetching modes for ${lines.length} lines`);
      
      for (const line of lines) {
        try {
          const response = await fetch(`${apiUrl}/lines/${line.id}/mode`);
          const result = await response.json();
          if (result.data && result.data.current_mode) {
            modes[line.id] = result.data.current_mode;
            console.log(`Line ${line.lineName} (${line.id}): ${result.data.current_mode}`);
          } else {
            modes[line.id] = 'stop';
            console.log(`Line ${line.lineName} (${line.id}): defaulting to stop`);
          }
        } catch (error) {
          console.error(`Error fetching mode for line ${line.id}:`, error);
          modes[line.id] = 'stop';
        }
      }
      
      console.log('Setting line modes:', modes);
      setLineModes(modes);
    } catch (error) {
      console.error('Error fetching line modes:', error);
    }
  };

  const fetchSystemDefaults = async () => {
    try {
      const response = await fetch(`${apiUrl}/system-defaults`);
      const result = await response.json();
      if (result.defaultNumberOfPockets) {
        setNumberOfPockets(result.defaultNumberOfPockets);
        console.log('Number of pockets from system defaults:', result.defaultNumberOfPockets);
      }
      if (result.defaultNumberOfStations) {
        setDefaultNumberOfStations(result.defaultNumberOfStations);
        console.log('Default number of stations from system defaults:', result.defaultNumberOfStations);
      }
    } catch (error) {
      console.error('Error fetching system defaults:', error);
    }
  };

  const updateSimulationStatus = () => {
    if (!currentSession || currentSession.status !== 'running') {
      setSimulationStatus('Simulation Service: Not Running');
      return;
    }

    // Count lines in each mode
    const modeCounts = {
      production: 0,
      'production-standby': 0,
      prepare: 0,
      stop: 0,
      clean: 0
    };
    
    Object.values(lineModes).forEach((mode: string) => {
      if (modeCounts.hasOwnProperty(mode)) {
        modeCounts[mode]++;
      }
    });
    
    // Build status message based on all lines
    const activeLines = modeCounts.production + modeCounts['production-standby'] + modeCounts.prepare;
    const totalLines = Object.keys(lineModes).length;
    
    if (activeLines === 0) {
      setSimulationStatus(`Simulation Running - All ${totalLines} lines paused (Stop/Clean modes)`);
    } else {
      const statusParts = [];
      if (modeCounts.production > 0) statusParts.push(`${modeCounts.production} in Production`);
      if (modeCounts['production-standby'] > 0) statusParts.push(`${modeCounts['production-standby']} in Standby`);
      if (modeCounts.prepare > 0) statusParts.push(`${modeCounts.prepare} in Prepare`);
      if (modeCounts.stop > 0) statusParts.push(`${modeCounts.stop} Stopped`);
      
      setSimulationStatus(`Simulation Running - ${statusParts.join(', ')}`);
    }
  };

  const fetchProductionData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/production-data/summary`);
      const result = await response.json();
      if (result.data) {
        setProductionData(result.data);
      }
    } catch (error) {
      console.error('Error fetching production data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHardwareData = async (type: 'infeed' | 'outfeed') => {
    setLoadingHardwareData(true);
    try {
      const response = await fetch(`${apiUrl}/hardware-data/${type}?limit=100`);
      const result = await response.json();
      if (result.data) {
        setHardwareDataRecords(result.data);
      }
    } catch (error) {
      console.error(`Error fetching ${type} hardware data:`, error);
    } finally {
      setLoadingHardwareData(false);
    }
  };

  const fetchHardwareDataWithFilters = async (page: number = 1) => {
    setLoadingHardwareData(true);
    try {
      const offset = (page - 1) * recordsPerPage;
      const params = new URLSearchParams();
      if (selectedHardwareLine) params.append('lineId', selectedHardwareLine);
      params.append('date', selectedHardwareDate);
      params.append('limit', recordsPerPage.toString());
      params.append('offset', offset.toString());
      
      // Add timestamp to prevent caching
      params.append('t', Date.now().toString());
      const response = await fetch(`${apiUrl}/hardware-data/${hardwareDataSubTab}?${params}`);
      const result = await response.json();
      if (result.data) {
        console.log(`Fetched ${hardwareDataSubTab} records:`, result.data.slice(0, 2));
        setHardwareDataRecords(result.data);
        // Estimate total records (since we don't have a count endpoint)
        // If we get a full page, assume there are more
        setTotalRecords(result.data.length === recordsPerPage ? page * recordsPerPage + 1 : (page - 1) * recordsPerPage + result.data.length);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error(`Error fetching ${hardwareDataSubTab} hardware data:`, error);
    } finally {
      setLoadingHardwareData(false);
    }
  };

  const clearProductionData = async (date: string, shift?: string, productGroupId?: string) => {
    if (!confirm(`Are you sure you want to clear production data for ${date}${shift ? ` - ${shift}` : ''}?`)) {
      return;
    }

    setDeletingItem(`${date}-${shift || 'all'}`);
    try {
      const queryParams = new URLSearchParams();
      if (shift) queryParams.append('shift', shift);
      if (productGroupId) queryParams.append('productGroupId', productGroupId);
      
      const response = await fetch(
        `${apiUrl}/production-data/clear/${date}${queryParams.toString() ? `?${queryParams}` : ''}`,
        { method: 'DELETE' }
      );
      
      const result = await response.json();
      if (result.message === 'success') {
        alert(`Cleared: ${result.data.timeTrackingDeleted} time records, ${result.data.productionDataDeleted} production records`);
        fetchProductionData();
      }
    } catch (error) {
      console.error('Error clearing production data:', error);
      alert('Failed to clear production data');
    } finally {
      setDeletingItem(null);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatWeight = (weight: number) => {
    return weight.toFixed(2) + ' lbs';
  };

  const addStationConfig = async () => {
    if (!selectedLine) {
      alert('Please select a line first');
      return;
    }

    const line = lines.find(l => l.id === selectedLine);
    if (!line) return;

    // Find next available station number
    const existingStations = simulationConfigs
      .filter(c => c.lineId === selectedLine)
      .map(c => c.stationNumber);
    
    let nextStation = 1;
    while (existingStations.includes(nextStation) && nextStation <= defaultNumberOfStations) {
      nextStation++;
    }

    // Check against the system default number of stations
    if (nextStation > defaultNumberOfStations) {
      alert(`Cannot add station ${nextStation}. Maximum number of stations is ${defaultNumberOfStations} (configured in Settings > System Defaults).`);
      return;
    }

    if (nextStation > line.stationCount) {
      alert('All stations are already configured for this line');
      return;
    }


    // Let the backend generate the realistic values
    const newConfig = {
      lineId: selectedLine,
      stationNumber: nextStation,
      enabled: true
      // Backend will generate: infeedRate, infeedVariance, pocketRates, pocketVariance
    };

    try {
      const response = await fetch(`${apiUrl}/simulation-configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig)
      });

      const result = await response.json();
      if (result.message === 'success') {
        fetchSimulationConfigs();
      }
    } catch (error) {
      console.error('Error adding station config:', error);
    }
  };

  const updateConfig = async (config: SimulationConfig) => {
    try {
      // Convert flat structure back to nested structure for API
      const apiConfig = {
        enabled: config.enabled,
        config: {
          infeedRate: config.infeedRate,
          infeedVariance: config.infeedVariance,
          pocketRates: config.pocketRates,
          pocketVariance: config.pocketVariance,
          updateIntervalSeconds: config.updateIntervalSeconds || 5
        }
      };
      
      const response = await fetch(`${apiUrl}/simulation-configs/${config.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiConfig)
      });

      const result = await response.json();
      if (result.message === 'success') {
        setEditingConfig(null);
        fetchSimulationConfigs();
      }
    } catch (error) {
      console.error('Error updating config:', error);
    }
  };

  const deleteConfig = async (id: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/simulation-configs/${id}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      if (result.message === 'success') {
        fetchSimulationConfigs();
      }
    } catch (error) {
      console.error('Error deleting config:', error);
    }
  };

  const startSimulation = async () => {
    try {
      // Get all lines that have enabled simulation configs
      const enabledLineIds = [...new Set(
        simulationConfigs
          .filter(config => config.enabled)
          .map(config => config.lineId)
      )];
      
      // Get active product group sessions for these lines
      const productGroupIds: string[] = [];
      // TODO: Fetch active product group sessions for each line
      
      const response = await fetch(`${apiUrl}/simulation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: `Simulation ${new Date().toLocaleString()}`,
          lineIds: enabledLineIds,
          productGroupIds: productGroupIds
        })
      });

      const result = await response.json();
      if (result.message === 'success') {
        fetchSimulationStatus();
      }
    } catch (error) {
      console.error('Error starting simulation:', error);
    }
  };

  const stopSimulation = async () => {
    try {
      const response = await fetch(`${apiUrl}/simulation/stop`, {
        method: 'POST'
      });

      const result = await response.json();
      if (result.message === 'success') {
        fetchSimulationStatus();
      }
    } catch (error) {
      console.error('Error stopping simulation:', error);
    }
  };

  const toggleAllStations = async (setWorking: boolean) => {
    try {
      // Only allow setting to working in production or production-standby modes
      // Check mode for the active line
      const currentMode = activeLineTab ? lineModes[activeLineTab] : 'stop';
      if (setWorking && (currentMode === 'stop' || currentMode === 'prepare' || currentMode === 'clean')) {
        alert(`Cannot set stations to Working in ${currentMode} mode. Please switch to Production or Production Standby mode.`);
        return;
      }

      const response = await fetch(`${apiUrl}/station-assignments/toggle-all`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: setWorking })
      });

      const result = await response.json();
      if (result.message === 'success') {
        setSetAllWorking(setWorking);
        alert(`All stations set to ${setWorking ? 'Working' : 'Not Working'}`);
      }
    } catch (error) {
      console.error('Error toggling all stations:', error);
      alert('Failed to toggle station status');
    }
  };


  // Group production data by date
  const groupedProductionData = productionData.reduce((acc, item) => {
    const date = item.production_date.split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {} as Record<string, ProductionDataSummary[]>);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-800">Diagnostics</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('production-data')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'production-data'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Production Data
          </button>
          <button
            onClick={() => setActiveTab('hardware')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'hardware'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Hardware
          </button>
          <button
            onClick={() => setActiveTab('io')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'io'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            System IO
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {/* Simulation Configuration content removed - now in Hardware Simulation subtab */}
      {activeTab === 'production-data' ? (
        <div className="space-y-6">
          {/* Hardware Service Status */}
          {hardwareServiceStatus && (
            <div className={`p-4 rounded-lg border ${
              hardwareServiceStatus.status === 'running'
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-800">Hardware Service Status</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Status: <span className="font-medium">{hardwareServiceStatus.status}</span> | 
                    Mode: <span className="font-medium">{hardwareServiceStatus.productionMode}</span> | 
                    Shift: <span className="font-medium">{hardwareServiceStatus.currentShift}</span> | 
                    Active Simulations: <span className="font-medium">{hardwareServiceStatus.activeSimulations}</span>
                  </p>
                </div>
                <button
                  onClick={fetchHardwareServiceStatus}
                  className="p-2 text-slate-600 hover:text-slate-800"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Simulation Controls */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-800">Simulation Control</h2>
              <div className="flex gap-2">
                {!currentSession || currentSession.status === 'stopped' ? (
                  <button
                    onClick={startSimulation}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    <PlayIcon className="w-5 h-5 mr-2" />
                    Start Simulation
                  </button>
                ) : (
                  <button
                    onClick={stopSimulation}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    <StopIcon className="w-5 h-5 mr-2" />
                    Stop Simulation
                  </button>
                )}
              </div>
            </div>

            {/* Simulation Status Display */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Selected Line Mode:</p>
                  <p className={`text-lg font-bold ${
                    (activeLineTab && lineModes[activeLineTab]) === 'production' ? 'text-green-600' :
                    (activeLineTab && lineModes[activeLineTab]) === 'production-standby' ? 'text-amber-600' :
                    (activeLineTab && lineModes[activeLineTab]) === 'prepare' ? 'text-yellow-600' :
                    (activeLineTab && lineModes[activeLineTab]) === 'stop' ? 'text-red-600' :
                    'text-blue-600'
                  }`}>
                    {activeLineTab ? 
                      lines.find(l => l.id === activeLineTab)?.lineName || 'Unknown Line' : 
                      'No Line Selected'}: {activeLineTab && lineModes[activeLineTab] ? 
                      lineModes[activeLineTab].charAt(0).toUpperCase() + lineModes[activeLineTab].slice(1).replace('-', ' ') : 
                      'Stop'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-700">Simulation Status:</p>
                  <p className={`text-sm ${
                    simulationStatus.includes('Active') ? 'text-green-600' :
                    simulationStatus.includes('Paused') ? 'text-yellow-600' :
                    'text-gray-600'
                  }`}>
                    {simulationStatus}
                  </p>
                </div>
              </div>
            </div>


            {currentSession && (
              <div className="text-sm text-slate-600">
                <p>Session: {currentSession.name}</p>
                <p>Status: {currentSession.status}</p>
                {currentSession.startedAt && (
                  <p>Started: {new Date(currentSession.startedAt).toLocaleString()}</p>
                )}
              </div>
            )}
          </div>

          {/* Station Configuration with Line Tabs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4">Station Configuration by Line</h2>
              
              {/* Line Tabs */}
              {lines.length > 0 && (
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-4">
                    {lines.map(line => (
                      <button
                        key={line.id}
                        onClick={() => setActiveLineTab(line.id)}
                        className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                          activeLineTab === line.id
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {line.lineName}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
            </div>

            {/* Add Station Button for Active Line */}
            {activeLineTab && (
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedLine(activeLineTab);
                    addStationConfig();
                  }}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Station to {lines.find(l => l.id === activeLineTab)?.lineName}
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <div className="space-y-4">
                {simulationConfigs
                  .filter(config => config.lineId === activeLineTab)
                  .map(config => (
                  <div key={config.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">
                        {config.lineName} - Station {config.stationNumber}
                      </h3>
                      <div className="flex gap-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => updateConfig({ ...config, enabled: e.target.checked })}
                            className="mr-2"
                          />
                          Enabled
                        </label>
                        <button
                          onClick={() => {
                            if (editingConfig === config.id) {
                              setEditingConfig(null);
                              setEditingValues(null);
                            } else {
                              setEditingConfig(config.id);
                              setEditingValues({...config});
                            }
                          }}
                          className="p-1 text-slate-600 hover:text-slate-800"
                        >
                          <CogIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deleteConfig(config.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {editingConfig === config.id && editingValues && (
                      <div className="mt-4 space-y-3 border-t pt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Infeed Rate (lbs/min)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={(editingValues.infeedRate / 60).toFixed(2)}
                            onChange={(e) => setEditingValues({ 
                              ...editingValues, 
                              infeedRate: parseFloat(e.target.value) * 60 
                            })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          {Object.entries(editingValues.pocketRates || {}).map(([pocket, rate]) => (
                            <div key={pocket}>
                              <label className="block text-xs font-medium text-gray-700 capitalize">
                                {pocket} (%)
                              </label>
                              <input
                                type="number"
                                step="0.1"
                                value={rate}
                                onChange={(e) => setEditingValues({
                                  ...editingValues,
                                  pocketRates: {
                                    ...editingValues.pocketRates,
                                    [pocket]: parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          Total Yield: {Object.values(editingValues.pocketRates || {}).reduce((sum, rate) => sum + (rate as number), 0).toFixed(1)}% 
                          ({((editingValues.infeedRate / 60) * Object.values(editingValues.pocketRates || {}).reduce((sum, rate) => sum + (rate as number), 0) / 100).toFixed(2)} lbs/min output)
                        </div>
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => {
                              updateConfig(editingValues);
                              setEditingConfig(null);
                              setEditingValues(null);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingConfig(null);
                              setEditingValues(null);
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'production-data' ? (
        <div className="space-y-6">
          {/* Production Data Summary */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-800">Production Data Summary</h2>
                <button
                  onClick={fetchProductionData}
                  className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-800"
                >
                  <ArrowPathIcon className="w-5 h-5 mr-2" />
                  Refresh
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-6 text-center">Loading production data...</div>
            ) : Object.keys(groupedProductionData).length === 0 ? (
              <div className="p-6 text-center text-gray-500">No production data found</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupedProductionData).map(([date, items]) => (
                  <div key={date} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-slate-800">
                        {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </h3>
                      <button
                        onClick={() => clearProductionData(date)}
                        disabled={deletingItem === `${date}-all`}
                        className="flex items-center px-3 py-1 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 disabled:opacity-50"
                      >
                        {deletingItem === `${date}-all` ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                            Clearing...
                          </>
                        ) : (
                          <>
                            <TrashIcon className="w-4 h-4 mr-1" />
                            Clear All
                          </>
                        )}
                      </button>
                    </div>

                    <div className="space-y-3">
                      {items.map((item, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-slate-800">
                                {item.product_group_name || 'No Product Group'}
                              </span>
                              <span className="ml-2 text-sm text-slate-600">
                                ({item.shift})
                              </span>
                            </div>
                            <button
                              onClick={() => clearProductionData(date, item.shift, item.product_group_id)}
                              disabled={deletingItem === `${date}-${item.shift}`}
                              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              {deletingItem === `${date}-${item.shift}` ? 'Clearing...' : 'Clear'}
                            </button>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center">
                              <UsersIcon className="w-4 h-4 mr-2 text-gray-400" />
                              <div>
                                <div className="text-gray-600">Workers</div>
                                <div className="font-medium">{item.worker_count}</div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
                              <div>
                                <div className="text-gray-600">Time Worked</div>
                                <div className="font-medium">{formatDuration(item.total_seconds_worked)}</div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <ScaleIcon className="w-4 h-4 mr-2 text-gray-400" />
                              <div>
                                <div className="text-gray-600">Infeed</div>
                                <div className="font-medium">{formatWeight(item.total_infeed_weight)}</div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <ChartBarIcon className="w-4 h-4 mr-2 text-gray-400" />
                              <div>
                                <div className="text-gray-600">Outfeed</div>
                                <div className="font-medium">{formatWeight(item.total_outfeed_weight)}</div>
                              </div>
                            </div>
                          </div>

                          {item.data_points > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                              {item.data_points} data points | 
                              {item.line_count} lines | 
                              {item.station_count} stations
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Caution: Data Deletion</p>
                <p className="mt-1">
                  Clearing production data is permanent and cannot be undone. This will remove all time tracking, 
                  production metrics, and session data for the selected date and shift.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'hardware' ? (
        /* Hardware Tab */
        <div className="space-y-6">
          {/* Hardware Subtabs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex space-x-6 border-b border-gray-200 mb-6">
              <button
                onClick={() => setHardwareSubTab('mqtt-sparkplug')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  hardwareSubTab === 'mqtt-sparkplug'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                MQTT Sparkplug
              </button>
              <button
                onClick={() => setHardwareSubTab('hardware-data')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  hardwareSubTab === 'hardware-data'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Hardware Data
              </button>
              <button
                onClick={() => setHardwareSubTab('hardware-registry')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  hardwareSubTab === 'hardware-registry'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Hardware Registry
              </button>
              <button
                onClick={() => setHardwareSubTab('hardware-simulation')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  hardwareSubTab === 'hardware-simulation'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Hardware Simulation
              </button>
              <button
                onClick={() => setHardwareSubTab('hardware-logging')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  hardwareSubTab === 'hardware-logging'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Hardware Logging
              </button>
            </div>

            {hardwareSubTab === 'mqtt-sparkplug' ? (
            /* MQTT Sparkplug Content */
            <div className="flex-1 overflow-hidden">
              <MqttProfessional />
            </div>
          ) : hardwareSubTab === 'hardware-registry' ? (
            /* Hardware Registry Content */
            <div className="flex-1 overflow-hidden">
              <HardwareRegistry />
            </div>
          ) : hardwareSubTab === 'hardware-simulation' ? (
            /* Hardware Simulation Content */
            <div className="flex-1 overflow-hidden">
              <HardwareSimulation />
            </div>
          ) : hardwareSubTab === 'hardware-logging' ? (
            /* Hardware Logging Content */
            <div className="flex-1 overflow-hidden">
              <HardwareLogging />
            </div>
          ) : (
            /* Hardware Data Content */
            <div>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">Hardware Data Records</h2>
              
              {/* Filters Section */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Line Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Production Line
                    </label>
                    <select
                      value={selectedHardwareLine}
                      onChange={(e) => setSelectedHardwareLine(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="">All Lines</option>
                      {lines.map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.lineName}
                          {line.hardwareId && ` (HW: ${line.hardwareId})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Production Date
                    </label>
                    <input
                      type="date"
                      value={selectedHardwareDate}
                      onChange={(e) => setSelectedHardwareDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  {/* Refresh Button */}
                  <div className="flex items-end">
                    <button
                      onClick={() => fetchHardwareDataWithFilters(1)}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Load Data
                    </button>
                  </div>
                </div>

                {/* Quick Filters */}
                <div className="mt-3 flex items-center space-x-4 text-sm">
                  <button
                    onClick={() => {
                      setSelectedHardwareDate(new Date().toISOString().split('T')[0]);
                      setTimeout(() => fetchHardwareDataWithFilters(1), 100);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      setSelectedHardwareDate(yesterday.toISOString().split('T')[0]);
                      setTimeout(() => fetchHardwareDataWithFilters(1), 100);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => {
                      const lastWeek = new Date();
                      lastWeek.setDate(lastWeek.getDate() - 7);
                      setSelectedHardwareDate(lastWeek.toISOString().split('T')[0]);
                      setTimeout(() => fetchHardwareDataWithFilters(1), 100);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Last Week
                  </button>
                </div>
              </div>
              
              {/* Infeed/Outfeed Subtabs */}
              <div className="flex space-x-4 border-b border-gray-200 mb-6">
                <button
                  onClick={() => {
                    setHardwareDataSubTab('infeed');
                    setTimeout(() => fetchHardwareDataWithFilters(1), 100);
                  }}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    hardwareDataSubTab === 'infeed'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Infeed Records
                </button>
                <button
                  onClick={() => {
                    setHardwareDataSubTab('outfeed');
                    setTimeout(() => fetchHardwareDataWithFilters(1), 100);
                  }}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    hardwareDataSubTab === 'outfeed'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Outfeed Records
                </button>
              </div>

              {/* Records Table */}
              {loadingHardwareData ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">Loading hardware data...</p>
                </div>
              ) : hardwareDataRecords.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ScaleIcon className="w-12 h-12 mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">No {hardwareDataSubTab} records found</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {selectedHardwareLine ? 'Try selecting a different line or date' : 'Select a line and click "Load Data"'}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Line
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hardware ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Station
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weight (g)
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weight (LB)
                        </th>
                        {hardwareDataSubTab === 'outfeed' && (
                          <>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Pocket
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Product
                            </th>
                          </>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Run Cycle
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transaction ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {hardwareDataRecords.map((record: any) => (
                        <tr key={record.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(record.received_at || record.created_at || record.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {record.line_name || `Line ${record.hardware_id}`}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-indigo-600">
                            {record.hardware_id || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {record.station_number || record.stationnumber || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {record.weight_grams ? parseFloat(record.weight_grams).toFixed(2) : 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {record.weight_pounds ? parseFloat(record.weight_pounds).toFixed(3) : 'N/A'}
                          </td>
                          {hardwareDataSubTab === 'outfeed' && (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {record.pocket_number || 'N/A'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {record.pocket_product_name || 'Unknown'}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {record.run_cycle || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-gray-500">
                            {record.transaction_id ? record.transaction_id.substring(0, 8) + '...' : 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.processed ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Processed
                              </span>
                            ) : record.error_message ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title={record.error_message}>
                                Error
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {/* Pagination Controls */}
              {hardwareDataRecords.length > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => fetchHardwareDataWithFilters(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <span className="text-sm text-gray-700">
                      Page {currentPage} {totalRecords > recordsPerPage && `of ${Math.ceil(totalRecords / recordsPerPage)}`}
                    </span>
                    
                    <button
                      onClick={() => fetchHardwareDataWithFilters(currentPage + 1)}
                      disabled={hardwareDataRecords.length < recordsPerPage}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        hardwareDataRecords.length < recordsPerPage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * recordsPerPage) + 1} to {((currentPage - 1) * recordsPerPage) + hardwareDataRecords.length} records
                  </div>
                </div>
              )}

              {/* Summary Stats */}
              {hardwareDataRecords.length > 0 && (
                <div className="mt-6 grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Total Records</p>
                    <p className="text-lg font-semibold text-gray-900">{hardwareDataRecords.length}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Processed</p>
                    <p className="text-lg font-semibold text-green-600">
                      {hardwareDataRecords.filter((r: any) => r.processed).length}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Errors</p>
                    <p className="text-lg font-semibold text-red-600">
                      {hardwareDataRecords.filter((r: any) => r.error_message).length}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Avg Weight</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {(hardwareDataRecords.reduce((sum, r: any) => 
                        sum + (parseFloat(r.weight_pounds) || 0), 0
                      ) / hardwareDataRecords.length).toFixed(2)} LB
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      ) : null}

      {/* IO Tab Content */}
      {activeTab === 'io' && (
        <div className="mt-4">
          <IODiagnostics />
        </div>
      )}

    </div>
  );
};

export default DiagnosticsPage;